import { BaseService } from "@/services/BaseService";
import {
  AdminAnalyticsPreferencesBlob,
  AdminAnalyticsPreferencesResponseDto,
  PlatformAnalyticsExportFilters,
  PlatformAnalyticsRefreshRunDto,
  PlatformAnalyticsSummaryFilters,
  PlatformAnalyticsSummaryResponseDto,
  ReportScheduleConfigDto,
  TriggerAnalyticsRefreshRequest,
  UpdateReportScheduleRequest,
} from "@/types/admin-analytics";

interface IAdminAnalyticsService {
  getSummary(
    filters: PlatformAnalyticsSummaryFilters,
  ): Promise<PlatformAnalyticsSummaryResponseDto>;
  triggerRefresh(
    body: TriggerAnalyticsRefreshRequest,
  ): Promise<PlatformAnalyticsRefreshRunDto>;
  getRefreshStatus(
    runId?: string,
  ): Promise<PlatformAnalyticsRefreshRunDto | null>;
  exportCsv(filters: PlatformAnalyticsExportFilters): Promise<Blob>;
  getPreferences(): Promise<AdminAnalyticsPreferencesResponseDto>;
  updatePreferences(
    preferences: AdminAnalyticsPreferencesBlob,
  ): Promise<AdminAnalyticsPreferencesResponseDto>;
  getReportSchedules(): Promise<ReportScheduleConfigDto[]>;
  updateReportSchedule(
    config: UpdateReportScheduleRequest,
  ): Promise<ReportScheduleConfigDto>;
}

class AdminAnalyticsService
  extends BaseService
  implements IAdminAnalyticsService
{
  private basePath = "/admin/analytics";

  async getSummary(
    filters: PlatformAnalyticsSummaryFilters,
  ): Promise<PlatformAnalyticsSummaryResponseDto> {
    return this.get<PlatformAnalyticsSummaryResponseDto>(
      `${this.basePath}/summary`,
      {
        period: filters.period,
        date: filters.date || undefined,
        compare: filters.compare ? "true" : undefined,
      },
    );
  }

  /**
   * Returns 202 with the newly-created run in RUNNING status. 409 when a
   * refresh is already running — toasting is left to the hook layer so the
   * conflict gets a contextual message instead of the raw server error.
   */
  async triggerRefresh(
    body: TriggerAnalyticsRefreshRequest,
  ): Promise<PlatformAnalyticsRefreshRunDto> {
    return this.request<PlatformAnalyticsRefreshRunDto>(
      `${this.basePath}/refresh`,
      {
        method: "POST",
        body: JSON.stringify(body),
        disableToast: true,
      },
    );
  }

  /** Without `runId`, returns the latest run by startedAt — or `null` if no run ever happened. */
  async getRefreshStatus(
    runId?: string,
  ): Promise<PlatformAnalyticsRefreshRunDto | null> {
    return this.get<PlatformAnalyticsRefreshRunDto | null>(
      `${this.basePath}/refresh/status`,
      { runId: runId || undefined },
    );
  }

  async exportCsv(filters: PlatformAnalyticsExportFilters): Promise<Blob> {
    return this.getBlob(`${this.basePath}/export`, {
      period: filters.period,
      date: filters.date || undefined,
    });
  }

  async getPreferences(): Promise<AdminAnalyticsPreferencesResponseDto> {
    return this.get<AdminAnalyticsPreferencesResponseDto>(
      `${this.basePath}/preferences`,
    );
  }

  /** Full upsert/replace of the blob — always send the complete preferences object. */
  async updatePreferences(
    preferences: AdminAnalyticsPreferencesBlob,
  ): Promise<AdminAnalyticsPreferencesResponseDto> {
    return this.put<AdminAnalyticsPreferencesResponseDto>(
      `${this.basePath}/preferences`,
      { preferences },
    );
  }

  /** Returns config for both periods; unconfigured rows are virtual defaults. */
  async getReportSchedules(): Promise<ReportScheduleConfigDto[]> {
    return this.get<ReportScheduleConfigDto[]>(
      `${this.basePath}/report-schedule`,
    );
  }

  /** Configures one period per call — full replace of that period's config. */
  async updateReportSchedule(
    config: UpdateReportScheduleRequest,
  ): Promise<ReportScheduleConfigDto> {
    return this.put<ReportScheduleConfigDto>(
      `${this.basePath}/report-schedule`,
      config,
    );
  }
}

export const adminAnalyticsService = new AdminAnalyticsService();
